import { Link } from 'react-router-dom';
import { makeStyles, tokens, Title2, Title3, Body1, Caption1 } from '@fluentui/react-components';
import type { UserRole } from '../../api/types';
import { useMeProfile } from '../../api/hooks';

// Programme-level reports index. Each entry is a tile that links to one of the FEAT-015
// — FEAT-023 / FEAT-029 internal reports. Tiles are added as each report ships; until
// then, "Coming soon" is shown so internal users can see what's on the way without
// clicking dead links.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    transition: 'box-shadow 0.15s ease',
    '&:hover': { boxShadow: tokens.shadow8 },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  tileDisabled: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    pointerEvents: 'none',
  },
  badge: { color: tokens.colorNeutralForeground3 },
});

interface ReportEntry {
  id: string;
  to: string | null; // null → coming soon
  title: string;
  description: string;
  feat: string;
}

const REPORTS: ReportEntry[] = [
  { id: 'burnup',              to: '/reports/programme/burnup',           title: 'Programme Burnup & Forecast', description: 'Scope vs completed with a P50/P85/P95 forecast cone.',          feat: 'FEAT-015' },
  { id: 'concurrent-projects', to: '/reports/programme/concurrent-projects', title: 'Concurrent Projects Timeline', description: 'Every active project on one RAG-coloured timeline.',           feat: 'FEAT-016' },
  { id: 'wsjf',                to: '/reports/programme/wsjf',              title: 'WSJF Backlog',                 description: 'Backlog ranked by economic priority (BV + TC) / Effort.',     feat: 'FEAT-017' },
  { id: 'value-at-risk',       to: '/reports/programme/value-at-risk',     title: 'Value at Risk',                description: '2D heat-map highlighting high-value high-risk items.',         feat: 'FEAT-018' },
  { id: 'bug-aging',           to: '/reports/programme/bug-aging',         title: 'Bug Aging',                    description: 'Open Bugs by age band and Severity.',                          feat: 'FEAT-020' },
  { id: 'cost-vs-estimate',    to: '/reports/programme/cost-vs-estimate',  title: 'Cost vs Estimate',             description: 'Margin tracker — approved estimate vs delivered cost.',        feat: 'FEAT-022' },
  { id: 'activity-heatmap',    to: '/reports/programme/activity',          title: 'Activity Heatmap',             description: '90-day calendar heatmap of WI activity per project.',          feat: 'FEAT-023' },
  { id: 'cfd',                 to: '/reports/programme/cfd',               title: 'Cumulative Flow',              description: 'Stacked-area chart of WIs per state, last 90 days.',           feat: 'FEAT-019' },
  { id: 'spec-quality',        to: '/reports/programme/spec-quality',      title: 'Spec Quality Scorecard',       description: 'Definition-of-Ready score per Feature, worst-first.',          feat: 'FEAT-021' },
  { id: 'cycle-time',          to: '/reports/programme/cycle-time',        title: 'WIP Cycle Time',               description: 'Total cycle time distribution (measure-only).',                feat: 'FEAT-029' },
];

export function ProgrammeIndex() {
  const styles = useStyles();
  const { data: profile } = useMeProfile();

  if (profile && !isInternal(profile.role)) {
    return <Body1>These reports are internal only.</Body1>;
  }

  return (
    <div className={styles.page}>
      <Title2>Programme Reports</Title2>
      <Body1 className={styles.hint}>
        Internal-only dashboards for delivery management. Customer-facing equivalents live under <Link to="/reports/customer">For Customer</Link>.
      </Body1>
      <div className={styles.grid}>
        {REPORTS.map((r) => (
          r.to ? (
            <Link key={r.id} to={r.to} className={styles.tile} aria-label={`${r.title} — ${r.feat}`}>
              <Title3>{r.title}</Title3>
              <Body1>{r.description}</Body1>
              <Caption1 className={styles.badge}>{r.feat}</Caption1>
            </Link>
          ) : (
            <div key={r.id} className={`${styles.tile} ${styles.tileDisabled}`} aria-label={`${r.title} — coming soon`}>
              <Title3>{r.title}</Title3>
              <Body1>{r.description}</Body1>
              <Caption1 className={styles.badge}>{r.feat} · Coming soon</Caption1>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// Local helpers — kept here rather than in a shared util because role-gate logic is
// already a stable contract and inlining keeps the page self-contained.
function isInternal(role: UserRole): boolean {
  return role === 'SystemAdmin' || role === 'Administrator';
}
